import useEventListener from '../../hooks/useEventListener.hooks';

/**
 * HITL 3.0 intent fast-path: Alt+1…9 fire the intent with that shortcut number,
 * matching the on-pill badges. Numbering is contiguous over the keyboard-
 * reachable intents (Request Staff is excluded), so there is no gap.
 *
 * Alt (not a bare digit) is deliberate:
 *  - it never collides with typing in the Quick Response box, so the shortcut
 *    works even mid-message — no "is an input focused?" guard needed;
 *  - it doesn't fight Ctrl push-to-talk (`Speech/SpeechToText.ts`);
 *  - `preventDefault()` stops macOS Option+digit from inserting a glyph.
 *
 * `event.code` (Digit1…Digit9) is matched so it stays layout-independent and
 * unaffected by Alt remapping the produced character. Out-of-range digits are
 * no-ops — `fireIntentByShortcut` owns that guard.
 *
 * HITL3-only: mounted from `IntentsSectionGrouped`, which renders solely in the
 * new layout.
 */
const useIntentHotkeys = (fireIntentByShortcut: (shortcut: number) => void) => {
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    const match = /^Digit([1-9])$/.exec(e.code);
    if (!match) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    fireIntentByShortcut(Number(match[1]));
  };

  // Capture phase so the focused omnibar can't swallow the key first (and macOS
  // Option+digit never reaches the input to insert a glyph).
  useEventListener('keydown', handleKeyDown, window, true);
};

export default useIntentHotkeys;
